<script>
    import TaskGroupModel from 'models/TaskGroupModel';
    import {onDestroy, tick} from 'svelte';

    export let activeItemKey
    export let itemKey = Infinity
    export let model = new Backbone.Model()
    export let disabled = null

    let isActive = false

    let itemElement

    activeItemKey.subscribe((value) => {
        isActive = itemKey === value
        scrollIntoView()
    })

    scrollIntoView()

    const activityModel = model.getActivityModel?.()

    let status
    setStatus()

    if (activityModel) {
        // Update status when the activity-wide 'showScores' flag is changed in linear activity.
        activityModel.on('change:showScores', setStatus)
        // Update status when score of or saved stated (server, local) of a response changes.
        activityModel.responses.on('change:score change:saved_state', setStatus)
    }
    // Update statgus when the taskgroup-specific 'showScores' flag is enabled when clicking in
    // the 'show scores' button in an adaptive activity task group.
    model.on('change:showScores', setStatus)

    function setStatus() {
        if (
            !activityModel ||
            !(model instanceof TaskGroupModel) ||
            model.tasks.length === 0
        ) {
            return
        }

        const {countChecked, countMade, score} = model.getProgress(Backbone.Model.user, false)
        if (
            // If user is student or supervisor
            ACL.checkRole([ACL.roles.STUDENT, ACL.roles.SUPERVISOR]) &&
            // If answers are set to visible in this activity.
            activityModel.get('show_answers') &&
            // If the activity or task group has the showScores flag.
            // Or if the activity is of an exam type, applicable for student exam answer review mode.
            (
                activityModel.get('showScores') || model.get('showScores') || activityModel.isExam()
            ) &&
            // If number of tasks checked equals number of tasks in the task group.
            countChecked === model.tasks.length
        ) {
            if (score === 0) {
                status = 'isIncorrect'
            } else if (score === countChecked) {
                status = 'isCorrect'
            } else {
                status = 'isSomewhatCorrect'
            }
        } else if (countMade === model.tasks.length) {
            status = 'isFinished'
        } else {
            status = 'hasTasks'
        }
    }

    function setActive() {
        activeItemKey.set(itemKey)
    }

    async function scrollIntoView() {
        if (!itemElement) {
            await tick()
        }
        if (isActive && itemElement) {
            itemElement.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            })
        }
    }

    onDestroy(() => {
        if (activityModel) {
            activityModel.off('change:showScores', setStatus)
            activityModel.responses.off('change:score add', setStatus)
        }
        model.off('change:showScores', setStatus)
    })
</script>

<button
    bind:this={itemElement}
    class:is-active={isActive}
    {disabled}
    on:click={setActive}
>
    <div class="index"><slot></slot></div>
    <div
        class="status"
        class:has-status={status !== null}
        class:status--has-tasks={status === 'hasTasks'}
        class:status--is-finished={status === 'isFinished'}
        class:status--is-incorrect={status === 'isIncorrect'}
        class:status--is-somewhat-correct={status === 'isSomewhatCorrect'}
        class:status--is-correct={status === 'isCorrect'}
    ></div>
</button>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
button {
  user-select: none;
  position: relative;
  display: flex;
  align-items: stretch;
  background-color: #fff;
  min-height: 40px;
  width: 40px;
  border: 1px solid hsl(40, 4%, 84%);
  color: rgba(18, 45, 64, 0.5);
  fill: currentColor;
  border-radius: 12px;
}
button.is-active {
  color: #fff;
  background-color: hsl(205, 60%, 44%);
  border-color: hsl(205, 60%, 44%);
}
button.is-active:before {
  content: "";
  border: solid transparent;
  border-right: solid hsl(205, 60%, 44%);
  border-width: 6px;
  position: absolute;
  transform: translateY(-50%);
  top: 50%;
  right: 100%;
}
button:hover:not(.is-active):not(:disabled) {
  color: #122d40;
}
button:disabled {
  opacity: 0.5;
}

.index {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 0 auto;
  font-size: 16px;
}
.index > :global(svg) {
  width: 20px;
  height: 20px;
}

.status {
  display: none;
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.status.has-status {
  display: flex;
}
.status--has-tasks {
  background-color: #fff;
  border: 2px solid #122d40;
}
.status--is-finished {
  background-color: #122d40;
}
.status--is-incorrect {
  background-color: #fc7787;
}
.status--is-somewhat-correct {
  background-color: #ffa008;
}
.status--is-correct {
  background-color: #3fd17d;
}</style>
